// frontend/src/components/layout/navbar/user-menu.tsx
//
// The account menu, in one module: the shared panel plus the two triggers that
// open it.
//
//   - `UserMenuContent` — the panel (identity, Account/Billing, theme,
//     language, Logout). Private on purpose: it is only ever meaningful behind
//     a trigger, so exporting it would invite a third copy of the trigger
//     problem.
//   - `HeaderUserMenu`  — the avatar button at the right of the top bar. What
//     the app shell renders today.
//   - `NavUser`         — the wide sidebar row (avatar + name + email).
//
// Both triggers render the same `UserMenuContent`, which is the whole point:
// the menu's items exist once, so the two entry points cannot drift apart.
// They live together rather than in separate files because splitting them buys
// nothing — the panel has no other consumer, and a `header/` copy would still
// have to import back into `navbar/`.
import {
    BadgeCheck,
    ChevronsUpDown,
    CreditCard,
    Languages,
    LogOut,
    Monitor,
    Moon,
    Sun,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FlagImage } from 'react-international-phone';
import { Link } from 'react-router-dom';
import { useTheme } from 'next-themes';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar';
import { useCurrentUser } from '@/features/auth/hooks/use-current-user';
import { useLogout } from '@/features/auth/hooks/use-logout';
import type { IUser } from '@/features/auth/interfaces';
import { LANGUAGES, resolveLanguage } from '@/lib/languages';
import { ROUTES } from '@/router/constant/routes';
import { getFullName, getInitials } from '@/utils/string';

interface UserMenuContentProps {
    readonly user: IUser;
    /** Where the panel opens relative to its trigger. */
    readonly side?: 'top' | 'right' | 'bottom' | 'left';
    readonly align?: 'start' | 'center' | 'end';
    readonly className?: string;
    /** Fired when a menu link navigates — lets a caller close the mobile sidebar. */
    readonly onNavigate?: () => void;
}

function UserMenuContent({
    user,
    side = 'bottom',
    align = 'end',
    className = 'min-w-56 rounded-lg',
    onNavigate,
}: UserMenuContentProps) {
    const { t, i18n } = useTranslation();
    const { theme, setTheme } = useTheme();
    const logoutMutation = useLogout();

    const MenuIcon = theme === 'system' ? Monitor : theme === 'dark' ? Moon : Sun;
    const activeLanguage = resolveLanguage(i18n.language);
    const fullName = getFullName(user.firstName, user.lastName);

    return (
        <DropdownMenuContent className={className} side={side} align={align} sideOffset={4}>
            <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                        <AvatarImage src={user.avatarUrl ?? ''} />
                        <AvatarFallback className="rounded-full">
                            {getInitials(fullName)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="text-text-primary truncate font-medium">{fullName}</span>
                        <span className="text-text-secondary truncate text-xs">{user.email}</span>
                    </div>
                </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                    <Link to={ROUTES.PROFILE} onClick={onNavigate}>
                        <BadgeCheck className="hover:text-sidebar-primary" />
                        {t('user.account')}
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                    <Link to={ROUTES.BILLING} onClick={onNavigate}>
                        <CreditCard className="hover:text-sidebar-primary" />
                        {t('user.billing')}
                    </Link>
                </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
                <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                        <MenuIcon className="hover:text-sidebar-primary mr-2 size-4" />
                        {t('user.themeMode')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                        <DropdownMenuItem onClick={() => setTheme('light')}>
                            <Sun className="hover:text-sidebar-primary" />
                            {t('user.lightMode')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTheme('dark')}>
                            <Moon className="hover:text-sidebar-primary" />
                            {t('user.darkMode')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setTheme('system')}>
                            <Monitor className="hover:text-sidebar-primary" />
                            {t('user.systemMode')}
                        </DropdownMenuItem>
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
                {/* Sibling of Theme Mode: both are display preferences, and both
                    persist to this browser only (i18next caches the choice in
                    localStorage) rather than to the user record. */}
                <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                        <Languages className="hover:text-sidebar-primary mr-2 size-4" />
                        {t('language.label', 'Language')}
                        {/* The current language reads out on the closed row, so
                            it takes no click to answer "which one am I on?". */}
                        <span className="text-text-secondary ms-auto ps-4 text-xs">
                            {activeLanguage.nativeLabel}
                        </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                        <DropdownMenuRadioGroup
                            value={activeLanguage.code}
                            onValueChange={(code) => void i18n.changeLanguage(code)}
                        >
                            {LANGUAGES.map((language) => (
                                <DropdownMenuRadioItem
                                    key={language.code}
                                    value={language.code}
                                    // `lang` lets a screen reader switch voice for
                                    // the endonym instead of spelling it out.
                                    lang={language.code}
                                >
                                    <FlagImage
                                        iso2={language.iso2}
                                        style={{ width: '20px', height: '15px', flexShrink: 0 }}
                                    />
                                    <span>{language.nativeLabel}</span>
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logoutMutation.mutate()}>
                <LogOut />
                {t('user.logout')}
            </DropdownMenuItem>
        </DropdownMenuContent>
    );
}

/**
 * Header trigger: a bare avatar at the right end of the top bar, deliberately
 * larger than its neighbouring icon buttons so it reads as the anchor of the
 * control cluster rather than one more icon in the row.
 */
export function HeaderUserMenu() {
    const { t } = useTranslation();
    const { data: user } = useCurrentUser();

    // Nothing to trigger until the session resolves; the header keeps its shape
    // because every sibling control is fixed-width.
    if (!user) return null;

    const fullName = getFullName(user.firstName, user.lastName);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-lg"
                    className="rounded-full p-0 hover:bg-transparent"
                    aria-label={t('user.account')}
                >
                    <Avatar className="size-10">
                        <AvatarImage src={user.avatarUrl ?? ''} alt="" />
                        <AvatarFallback className="bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-200 text-sm font-semibold">
                            {getInitials(fullName)}
                        </AvatarFallback>
                    </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <UserMenuContent user={user} side="bottom" align="end" />
        </DropdownMenu>
    );
}

/**
 * Sidebar-row trigger: avatar, name and email across a full-width row.
 *
 * The shell shows the account menu from the header instead and gives the
 * sidebar footer to the product branding, so nothing renders this today. It
 * stays as the wide-trigger variant for any surface that wants it — and
 * because it shares `UserMenuContent`, it can never fall out of step.
 */
export function NavUser({ user }: { user: IUser }) {
    const { isMobile, setOpenMobile } = useSidebar();
    const fullName = getFullName(user.firstName, user.lastName);

    const handleClick = () => {
        if (isMobile) {
            setOpenMobile(false);
        }
    };

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-secondary data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarImage src={user.avatarUrl ?? ''} />
                                <AvatarFallback>{getInitials(fullName)}</AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-medium">{fullName}</span>
                                <span className="truncate text-xs">{user.email}</span>
                            </div>
                            <ChevronsUpDown className="ml-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <UserMenuContent
                        user={user}
                        side={isMobile ? 'bottom' : 'right'}
                        align="end"
                        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                        onNavigate={handleClick}
                    />
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
